import CustomButton from "@components/buttons/CustomButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import { MerchantDropdown } from "@components/dropdowns/MerchantDropdown";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import { institutionApis } from "@services/apis/Institution";
import { GetCpgCommsisionListData } from "@services/models/Institution/GetCpgCommisionListModel";
import { GetP2PFeesData } from "@services/models/Institution/GetP2PFeesModel";
import { InstitutionMerchantConfig } from "@services/models/Institution/InstitutionMerchantConfigModel";
import { GetCryptoCurrencyListData } from "@services/models/Transactions/GetCryptoCurrencyListModel";
import { GetFiatCurrencyListData } from "@services/models/Transactions/GetFiatCurrenciesModel";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";
import {
  getGatewayForAccountCategory,
  isPayinFeeCategory,
  isPayoutFeeCategory,
  supportsNtxpay,
  supportsRampRates,
  usesTemplateId,
} from "../utils/accountCategories";
import AddCommissionSlider from "./AddCpgCommsionSlider";
import AddCrossRampIndiaFeesSlider from "./AddCrossRampIndiaFeesSlider";

interface MerchantConfigPanelProps {
  merchantId: number;
  userId: number;
  accountCategoryId: number;
  accountCategoryName: string;
  onConfigChanged?: () => void;
}

const inputStyle =
  "py-1 px-2 h-8 text-xs border border-border-subtle rounded focus:outline-none focus:ring-1 focus:ring-blue-500 w-full";
const selectStyle =
  "h-8 text-xs border border-border-subtle rounded focus:outline-none focus:ring-1 focus:ring-blue-500 w-full";

type CryptoListItem = GetCryptoCurrencyListData & {
  p2pCryptoCurrencyId?: number;
  id?: number;
  symbol?: string;
};

const toIdString = (value: unknown) =>
  value === undefined || value === null || value === "" ? "" : String(value);

const getCryptoCandidateIds = (crypto: CryptoListItem) =>
  [crypto.p2pCryptoCurrencyId, crypto.cryptoCurrencyId, crypto.id]
    .map(toIdString)
    .filter(Boolean);

const getCryptoOptionId = (crypto: CryptoListItem) =>
  getCryptoCandidateIds(crypto)[0] || "";

const cryptoMatchesId = (crypto: CryptoListItem, selectedId: string) =>
  getCryptoCandidateIds(crypto).includes(String(selectedId));

const getCryptoOptionLabel = (crypto: CryptoListItem, fallbackId: string) =>
  crypto.cryptoCurrencySymbol || crypto.symbol || fallbackId;

const asCurrencyList = <T,>(payload: unknown): T[] => {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === "object") {
    const nested = payload as { data?: unknown; list?: unknown };
    if (Array.isArray(nested.data)) return nested.data as T[];
    if (Array.isArray(nested.list)) return nested.list as T[];
  }
  return [];
};

const MerchantConfigPanel: React.FC<MerchantConfigPanelProps> = ({
  merchantId,
  userId,
  accountCategoryId,
  accountCategoryName,
  onConfigChanged,
}) => {
  const gateway = getGatewayForAccountCategory(accountCategoryId);
  const needsTemplate = usesTemplateId(accountCategoryId);
  const needsRamp = supportsRampRates(accountCategoryId);
  const needsNtxpay = supportsNtxpay(accountCategoryId);
  const needsPayinFee = isPayinFeeCategory(accountCategoryId);
  const needsPayoutFee = isPayoutFeeCategory(accountCategoryId);

  const [fetchingConfig, setFetchingConfig] = useState(true);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);
  const [configError, setConfigError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [isActive, setIsActive] = useState<0 | 1>(0);
  const [payinFee, setPayinFee] = useState("");
  const [payoutFee, setPayoutFee] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<number | undefined>(
    undefined,
  );

  const [hasSpecialRate, setHasSpecialRate] = useState("no");
  const [cryptoCurrency, setCryptoCurrency] = useState("");
  const [fiatCurrency, setFiatCurrency] = useState("");
  const [rate, setRate] = useState("");
  const [minCryptoAmount, setMinCryptoAmount] = useState("");
  const [maxCryptoAmount, setMaxCryptoAmount] = useState("");
  const [minFiatAmount, setMinFiatAmount] = useState("");
  const [maxFiatAmount, setMaxFiatAmount] = useState("");

  const [ntxPayFees, setNtxPayFees] = useState("");
  const [ntxType, setNtxType] = useState<"onramp" | "offramp" | "">("");

  const [p2pFees, setP2pFees] = useState<GetP2PFeesData[]>([]);
  const [cpgCommissions, setCpgCommissions] = useState<
    GetCpgCommsisionListData[]
  >([]);
  const [cryptoCurrencies, setCryptoCurrencies] = useState<CryptoListItem[]>(
    [],
  );
  const [fiatCurrencies, setFiatCurrencies] = useState<
    GetFiatCurrencyListData[]
  >([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [addP2PFeesSlider, setAddP2PFeesSlider] = useState(false);
  const [addCpgCommissionSlider, setAddCpgCommissionSlider] = useState(false);

  const { handleSuccess, handleError } = useSnackbarMessage();

  const resetForm = () => {
    setIsActive(0);
    setPayinFee("");
    setPayoutFee("");
    setSelectedTemplate(undefined);
    setHasSpecialRate("no");
    setCryptoCurrency("");
    setFiatCurrency("");
    setRate("");
    setMinCryptoAmount("");
    setMaxCryptoAmount("");
    setMinFiatAmount("");
    setMaxFiatAmount("");
    setNtxPayFees("");
    setNtxType("");
    setHasExistingConfig(false);
    setConfigError("");
  };

  const populateFromConfig = (config: InstitutionMerchantConfig) => {
    if (config.isActive !== undefined) {
      setIsActive(config.isActive);
    }

    if (config.fees !== undefined && config.fees !== null) {
      if (needsPayinFee) setPayinFee(String(config.fees));
      if (needsPayoutFee) setPayoutFee(String(config.fees));
    }

    if (config.templateId !== undefined) {
      setSelectedTemplate(config.templateId);
    }

    const hasRamp =
      config.rate !== undefined ||
      config.p2pCryptoCurrencyId !== undefined ||
      config.fiatCurrencyId !== undefined;

    if (hasRamp) {
      setHasSpecialRate("yes");
      if (
        config.p2pCryptoCurrencyId !== undefined &&
        config.p2pCryptoCurrencyId !== null
      ) {
        setCryptoCurrency(String(config.p2pCryptoCurrencyId));
      }
      if (
        config.fiatCurrencyId !== undefined &&
        config.fiatCurrencyId !== null
      ) {
        setFiatCurrency(String(config.fiatCurrencyId));
      }
      if (config.rate !== undefined) setRate(String(config.rate));
      if (config.minDestinationAmount !== undefined) {
        setMinCryptoAmount(String(config.minDestinationAmount));
      }
      if (config.maxDestinationAmount !== undefined) {
        setMaxCryptoAmount(String(config.maxDestinationAmount));
      }
      if (config.minSourceAmount !== undefined) {
        setMinFiatAmount(String(config.minSourceAmount));
      }
      if (config.maxSourceAmount !== undefined) {
        setMaxFiatAmount(String(config.maxSourceAmount));
      }
    }

    if (config.ntxpayFees !== undefined)
      setNtxPayFees(String(config.ntxpayFees));
    if (config.type) setNtxType(config.type);
  };

  const refreshConfig = async () => {
    try {
      setFetchingConfig(true);
      setConfigError("");
      resetForm();
      const response = await institutionApis.getInstitutionMerchantConfig({
        merchantId,
      });
      const config = response.data?.config;
      if (config) {
        const configured =
          config.fees !== undefined ||
          config.templateId !== undefined ||
          config.isActive !== undefined ||
          config.ntxpayFees !== undefined;
        setHasExistingConfig(configured);
        populateFromConfig(config);
        await fetchTemplatesAndCurrencies(
          config.templateId,
          config.p2pCryptoCurrencyId,
          config.fiatCurrencyId,
        );
      } else {
        setHasExistingConfig(false);
        await fetchTemplatesAndCurrencies();
      }
    } catch (error: any) {
      setHasExistingConfig(false);
      const message = error?.response?.data?.msg;
      if (message && message !== "Invalid merchantId") {
        setConfigError(message);
      }
      await fetchTemplatesAndCurrencies();
    } finally {
      setFetchingConfig(false);
    }
  };

  const fetchTemplatesAndCurrencies = async (
    existingTemplateId?: number,
    existingCryptoId?: number,
    existingFiatId?: number,
  ) => {
    try {
      setLoadingTemplates(true);
      const promises: Promise<void>[] = [];

      if (gateway === "UPI" || gateway === "PIX") {
        promises.push(
          institutionApis.getP2PFees().then((response) => {
            setP2pFees(response.data || []);
            if (
              existingTemplateId === undefined &&
              response.data?.[0]?.templateId !== undefined
            ) {
              setSelectedTemplate(response.data[0].templateId);
            }
          }),
        );
      }

      if (gateway === "CPG") {
        promises.push(
          institutionApis.getCPGCommissionsList().then((response) => {
            setCpgCommissions(response.data || []);
            if (
              existingTemplateId === undefined &&
              response.data?.[0]?.id !== undefined
            ) {
              setSelectedTemplate(response.data[0].id);
            }
          }),
        );
      }

      if (needsRamp) {
        promises.push(
          Promise.all([
            institutionApis.getP2PCryptoCurrencies(),
            institutionApis.getP2PFiatCurrencies(),
          ]).then(([cryptoResponse, fiatResponse]) => {
            const cryptos = asCurrencyList<CryptoListItem>(
              cryptoResponse?.data ?? cryptoResponse,
            );
            const fiats = asCurrencyList<GetFiatCurrencyListData>(
              fiatResponse?.data ?? fiatResponse,
            );
            setCryptoCurrencies(cryptos);
            setFiatCurrencies(fiats);

            if (existingCryptoId !== undefined && existingCryptoId !== null) {
              const selectedId = String(existingCryptoId);
              const match = cryptos.find((crypto) =>
                cryptoMatchesId(crypto, selectedId),
              );
              setCryptoCurrency(
                match ? getCryptoOptionId(match) || selectedId : selectedId,
              );
            }

            if (existingFiatId !== undefined && existingFiatId !== null) {
              const selectedId = String(existingFiatId);
              const match = fiats.find(
                (fiat) => String(fiat.fiatCurrencyId) === selectedId,
              );
              setFiatCurrency(
                match ? String(match.fiatCurrencyId) : selectedId,
              );
            }
          }),
        );
      }

      await Promise.all(promises);
    } catch (error) {
      handleError({ message: "Failed to load templates / currencies" });
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setFetchingConfig(true);
        setConfigError("");
        resetForm();
        let existingTemplateId: number | undefined;
        let existingCryptoId: number | undefined;
        let existingFiatId: number | undefined;
        try {
          const response = await institutionApis.getInstitutionMerchantConfig({
            merchantId,
          });
          const config = response.data?.config;
          if (config) {
            const configured =
              config.fees !== undefined ||
              config.templateId !== undefined ||
              config.isActive !== undefined ||
              config.ntxpayFees !== undefined;
            setHasExistingConfig(configured);
            populateFromConfig(config);
            existingTemplateId = config.templateId;
            existingCryptoId = config.p2pCryptoCurrencyId;
            existingFiatId = config.fiatCurrencyId;
          } else {
            setHasExistingConfig(false);
          }
        } catch (error: any) {
          setHasExistingConfig(false);
          const message = error?.response?.data?.msg;
          if (message && message !== "Invalid merchantId") {
            setConfigError(message);
          }
        } finally {
          setFetchingConfig(false);
        }
        await fetchTemplatesAndCurrencies(
          existingTemplateId,
          existingCryptoId,
          existingFiatId,
        );
      } catch {
        setFetchingConfig(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, accountCategoryId]);
  const isRampValid = () => {
    if (!needsRamp) return true;
    if (gateway === "UPI" && hasSpecialRate !== "yes") return true;

    const anyFilled =
      cryptoCurrency.trim() !== "" ||
      fiatCurrency.trim() !== "" ||
      rate.trim() !== "" ||
      minCryptoAmount.trim() !== "" ||
      maxCryptoAmount.trim() !== "" ||
      minFiatAmount.trim() !== "" ||
      maxFiatAmount.trim() !== "";

    // On update, ramp is optional unless the user starts editing it
    if (hasExistingConfig && !anyFilled) return true;

    return (
      cryptoCurrency.trim() !== "" &&
      fiatCurrency.trim() !== "" &&
      rate.trim() !== "" &&
      minCryptoAmount.trim() !== "" &&
      maxCryptoAmount.trim() !== "" &&
      minFiatAmount.trim() !== "" &&
      maxFiatAmount.trim() !== ""
    );
  };

  const isFormValid = () => {
    if (needsPayinFee && payinFee.trim() === "") return false;
    if (needsPayoutFee && payoutFee.trim() === "") return false;
    if (needsTemplate && selectedTemplate === undefined) return false;
    if (needsNtxpay && !hasExistingConfig) {
      if (ntxPayFees.trim() === "") {
        return false;
      }
    }
    return isRampValid();
  };

  const buildRampPayload = () => {
    if (!needsRamp) return {};
    if (gateway === "UPI" && hasSpecialRate !== "yes") return {};

    const anyFilled =
      cryptoCurrency.trim() !== "" ||
      fiatCurrency.trim() !== "" ||
      rate.trim() !== "";

    if (!anyFilled) return {};

    return {
      rate: Number(rate),
      p2pCryptoCurrencyId: Number(cryptoCurrency),
      fiatCurrencyId: Number(fiatCurrency),
      minSourceAmount: Number(minFiatAmount),
      maxSourceAmount: Number(maxFiatAmount),
      minDestinationAmount: Number(minCryptoAmount),
      maxDestinationAmount: Number(maxCryptoAmount),
    };
  };

  const buildNtxpayPayload = () => {
    if (!needsNtxpay) return {};
    const payload: Record<string, number> = {};
    if (ntxPayFees.trim()) payload.ntxpayFees = Number(ntxPayFees);
    return payload;
  };

  const handleActivate = async () => {
    try {
      setSubmitting(true);
      const payload: Record<string, unknown> = {
        merchantId,
        userId,
        ...buildRampPayload(),
        ...buildNtxpayPayload(),
      };

      if (needsPayinFee) payload.payinFee = Number(payinFee);
      if (needsPayoutFee) payload.payoutFee = Number(payoutFee);
      if (needsTemplate) payload.templateId = selectedTemplate;

      await institutionApis.activateInstitutionMerchantConfig(payload as any);
      handleSuccess({ message: "Merchant configured successfully" });
      await refreshConfig();
      onConfigChanged?.();
    } catch (error: any) {
      handleError({
        message:
          error?.response?.data?.msg ||
          "Failed to activate merchant config. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setSubmitting(true);
      const updates: Record<string, unknown> = {
        isActive,
        ...buildRampPayload(),
        ...buildNtxpayPayload(),
      };

      if (needsPayinFee && payinFee.trim() !== "") {
        updates.payinFee = Number(payinFee);
      }
      if (needsPayoutFee && payoutFee.trim() !== "") {
        updates.payoutFee = Number(payoutFee);
      }
      if (needsTemplate && selectedTemplate !== undefined) {
        updates.templateId = selectedTemplate;
      }

      await institutionApis.updateInstitutionMerchantConfig({
        merchantId,
        updates: updates as any,
      });
      handleSuccess({ message: "Config updated successfully" });
      await refreshConfig();
      onConfigChanged?.();
    } catch (error: any) {
      handleError({
        message:
          error?.response?.data?.msg ||
          "Failed to update merchant config. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderRampRates = () => {
    if (!needsRamp) return null;

    const showRampFields = gateway === "PIX" || hasSpecialRate === "yes";

    return (
      <>
        {gateway === "UPI" && (
          <div className="flex flex-row items-center justify-between">
            <label className="text-text-base font-medium text-[12px]">
              {hasExistingConfig
                ? "Update special rate?"
                : "Is there special rate?"}
            </label>
            <div className="w-[12.5%]">
              <select
                className={selectStyle}
                value={hasSpecialRate}
                onChange={(e) => setHasSpecialRate(e.target.value)}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
          </div>
        )}

        {gateway === "PIX" && (
          <label className="text-text-base font-medium text-[12px]">
            Ramp Rate Configuration
          </label>
        )}

        {showRampFields && (
          <div className="mb-2">
            <div className="flex flex-col border border-border-subtle rounded-lg">
              <div className="flex flex-row bg-bg-base px-2 py-2 rounded-t-lg">
                <span className="font-semibold text-[10px] w-[14%]">
                  Crypto Currency
                </span>
                <span className="font-semibold text-[10px] w-[14%]">
                  Fiat Currency
                </span>
                <span className="font-semibold text-[10px] w-[12%]">Rate</span>
                <span className="font-semibold text-[10px] w-[15%]">
                  Min Crypto
                </span>
                <span className="font-semibold text-[10px] w-[15%]">
                  Max Crypto
                </span>
                <span className="font-semibold text-[10px] w-[15%]">
                  Min Fiat
                </span>
                <span className="font-semibold text-[10px] w-[15%]">
                  Max Fiat
                </span>
              </div>
              <div className="flex flex-row bg-bg-base px-2 py-1 border-b border-border-subtle items-center">
                <div className="w-[14%] pr-1">
                  <select
                    className={selectStyle}
                    key={`crypto-${cryptoCurrency}-${cryptoCurrencies.length}`}
                    value={cryptoCurrency}
                    onChange={(e) => setCryptoCurrency(e.target.value)}
                  >
                    <option value="">Select Crypto</option>
                    {cryptoCurrency &&
                      !cryptoCurrencies.some((crypto) =>
                        cryptoMatchesId(crypto, cryptoCurrency),
                      ) && (
                        <option value={cryptoCurrency}>
                          ID: {cryptoCurrency}
                        </option>
                      )}
                    {cryptoCurrencies.map((crypto, index) => {
                      const id = getCryptoOptionId(crypto);
                      if (!id) return null;
                      return (
                        <option key={`${id}-${index}`} value={id}>
                          {getCryptoOptionLabel(crypto, id)}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="w-[14%] pr-1">
                  <select
                    className={selectStyle}
                    key={`fiat-${fiatCurrency}-${fiatCurrencies.length}`}
                    value={fiatCurrency}
                    onChange={(e) => setFiatCurrency(e.target.value)}
                  >
                    <option value="">Select Fiat</option>
                    {fiatCurrency &&
                      !fiatCurrencies.some(
                        (fiat) => String(fiat.fiatCurrencyId) === fiatCurrency,
                      ) && (
                        <option value={fiatCurrency}>ID: {fiatCurrency}</option>
                      )}
                    {fiatCurrencies.map((fiat) => (
                      <option
                        key={fiat.fiatCurrencyId}
                        value={String(fiat.fiatCurrencyId)}
                      >
                        {fiat.fiatCurrencySymbol}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-[12%] pr-1">
                  <input
                    className={inputStyle}
                    placeholder="Rate"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                  />
                </div>
                <div className="w-[15%] pr-1">
                  <input
                    className={inputStyle}
                    placeholder="Min Crypto"
                    value={minCryptoAmount}
                    onChange={(e) => setMinCryptoAmount(e.target.value)}
                  />
                </div>
                <div className="w-[15%] pr-1">
                  <input
                    className={inputStyle}
                    placeholder="Max Crypto"
                    value={maxCryptoAmount}
                    onChange={(e) => setMaxCryptoAmount(e.target.value)}
                  />
                </div>
                <div className="w-[15%] pr-1">
                  <input
                    className={inputStyle}
                    placeholder="Min Fiat"
                    value={minFiatAmount}
                    onChange={(e) => setMinFiatAmount(e.target.value)}
                  />
                </div>
                <div className="w-[15%] pr-1">
                  <input
                    className={inputStyle}
                    placeholder="Max Fiat"
                    value={maxFiatAmount}
                    onChange={(e) => setMaxFiatAmount(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderNtxpayFields = () => {
    if (!needsNtxpay) return null;
    return (
      <>
        {hasExistingConfig && ntxType && (
          <div className="flex flex-row items-center justify-between">
            <label className="text-text-base font-medium text-[12px]">
              Type
            </label>
            <span className="text-text-label text-[12px] font-medium">
              {ntxType}
            </span>
          </div>
        )}
        <GlobalTextInputField
          title={hasExistingConfig ? "NTX Pay Fees" : "NTX Pay Fees*"}
          type="number"
          value={ntxPayFees}
          onChange={(e) => setNtxPayFees(e.target.value)}
          placeholder="Enter NTX Pay fees"
        />
      </>
    );
  };

  if (!gateway) {
    return (
      <div className="w-[50%] flex items-center justify-center text-text-status-red text-[14px] font-medium">
        Unsupported account category: {accountCategoryId}
      </div>
    );
  }

  if (fetchingConfig) {
    return (
      <div className="flex flex-col border border-border-subtle py-2 px-3 rounded-lg gap-3 w-[50%]">
        <Skeleton height={24} />
        <Skeleton height={44} />
        <Skeleton height={44} />
        <Skeleton height={40} />
      </div>
    );
  }

  return (
    <div className="flex flex-col border border-border-subtle py-2 px-3 rounded-lg gap-3 w-[50%]">
      <div className="flex flex-row items-center justify-between">
        <p className="text-text-base text-[14px] font-semibold">
          {hasExistingConfig ? "Update" : "Activate"} {accountCategoryName}
        </p>
        <span className="text-[10px] text-text-label font-medium">
          {gateway} · Merchant #{merchantId}
        </span>
      </div>

      {configError && (
        <span className="text-text-status-red text-[12px]">{configError}</span>
      )}

      {hasExistingConfig && (
        <div className="flex flex-row items-center justify-between">
          <label className="text-text-base font-medium text-[12px]">
            Active Status
          </label>
          <div className="w-[30%]">
            <select
              className={selectStyle}
              value={isActive}
              onChange={(e) => setIsActive(Number(e.target.value) as 0 | 1)}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>
        </div>
      )}

      {needsPayinFee && (
        <GlobalTextInputField
          title="Payin Fee*"
          type="number"
          value={payinFee}
          onChange={(e) => setPayinFee(e.target.value)}
          placeholder="Enter payin fee"
        />
      )}

      {needsPayoutFee && (
        <GlobalTextInputField
          title="Payout Fee*"
          type="number"
          value={payoutFee}
          onChange={(e) => setPayoutFee(e.target.value)}
          placeholder="Enter payout fee"
        />
      )}

      {needsTemplate && (
        <>
          {loadingTemplates ? (
            <Skeleton height={44} className="m-0 p-0" />
          ) : gateway === "CPG" ? (
            <MerchantDropdown
              listOfValues={cpgCommissions.map((commission) => ({
                id: commission.id,
                name: `ID: ${commission.id} | Deposit Fee: ${Number(
                  commission.depositFee,
                ).toFixed(2)} | Withdrawal Fee: ${Number(
                  commission.withdrawalFee,
                ).toFixed(2)}`,
              }))}
              value={selectedTemplate}
              onChange={(value) => setSelectedTemplate(Number(value))}
              label="Select CPG Commission"
              color="bg-page-body"
              expandedBorderColor="border-button-primary"
              baseColor="bg-page-body"
              borderColor="border-subtle"
            />
          ) : (
            <MerchantDropdown
              listOfValues={p2pFees.map((fee) => ({
                id: fee.templateId,
                name: `ID: ${fee.templateId} | Currency ID: ${
                  fee.p2pCryptoCurrencyId
                } | Payin Fee: ${Number(fee.payInFees).toFixed(
                  2,
                )} | Payout Fee: ${Number(fee.payOutFees).toFixed(2)}`,
              }))}
              value={selectedTemplate}
              onChange={(value) => setSelectedTemplate(Number(value))}
              label="Select Template"
              color="bg-page-body"
              expandedBorderColor="border-button-primary"
              baseColor="bg-page-body"
              borderColor="border-subtle"
            />
          )}

          {gateway === "CPG" ? (
            <LightCustomButton
              text="+ Add Commission Template"
              isActive={true}
              onClick={() => setAddCpgCommissionSlider(true)}
            />
          ) : (
            <LightCustomButton
              text="+ Add Commission Template"
              isActive={true}
              onClick={() => setAddP2PFeesSlider(true)}
            />
          )}
        </>
      )}

      {renderRampRates()}
      {renderNtxpayFields()}

      <CustomButton
        text={hasExistingConfig ? "UPDATE CONFIG" : "SET TO ACTIVE"}
        isActive={isFormValid()}
        isButtonLoading={submitting}
        onClick={hasExistingConfig ? handleUpdate : handleActivate}
      />

      <AddCrossRampIndiaFeesSlider
        isOpen={addP2PFeesSlider}
        onClose={() => setAddP2PFeesSlider(false)}
        onConfirm={(success) => {
          if (success) fetchTemplatesAndCurrencies();
          setAddP2PFeesSlider(false);
        }}
      />
      <AddCommissionSlider
        isOpen={addCpgCommissionSlider}
        onClose={() => setAddCpgCommissionSlider(false)}
        onConfirm={(success) => {
          if (success) fetchTemplatesAndCurrencies();
          setAddCpgCommissionSlider(false);
        }}
      />
    </div>
  );
};

export default MerchantConfigPanel;
